if (!customElements.get('fs-sticky-cart')) {
  customElements.define('fs-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('.fs-sticky-cart__button');
      this.label = this.button.querySelector('span');
      this.quantity = this.querySelector('.fs-sticky-cart__quantity-input');
      this.options = this.querySelector('.fs-sticky-cart__options');
      this.price = this.querySelector('.fs-sticky-cart__current');
      this.compare = this.querySelector('.fs-sticky-cart__compare');
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.root.addEventListener('change', this.schedule, { signal });
      this.root.addEventListener('input', this.schedule, { signal });
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      this.quantity.addEventListener('input', () => this.updateQuantity(false), { signal });
      this.quantity.addEventListener('change', () => this.updateQuantity(true), { signal });
      this.button.addEventListener('click', () => this.add(), { signal });
      this.querySelector('.fs-sticky-cart__title').addEventListener('click', event => {
        event.preventDefault();
        this.showControls();
      }, { signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.root, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'min', 'max', 'step', 'value']
      });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.root);
      this.sync();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      document.body.style.removeProperty('--fs-sticky-cart-height');
    }

    get original() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    get originalQuantity() {
      return document.getElementById(`Quantity-${this.sectionId}`);
    }

    updateQuantity(commit) {
      const source = this.originalQuantity;
      if (!source || !this.quantity.validity.valid) return;
      source.value = this.quantity.value;
      if (commit) source.dispatchEvent(new Event('change', { bubbles: true }));
    }

    sync() {
      const source = this.original;
      const form = source?.form;
      if (!source || !form) {
        this.hidden = true;
        this.inert = true;
        return;
      }
      const busy = source.getAttribute('aria-disabled') === 'true' || source.classList.contains('loading');
      this.button.disabled = source.disabled || busy;
      this.button.setAttribute('aria-busy', String(busy));
      const label = source.querySelector('span')?.textContent.trim() || source.textContent.trim();
      if (this.label.textContent !== label) this.label.textContent = label;
      if (source.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', source.getAttribute('aria-haspopup'));

      const quantity = this.originalQuantity;
      this.quantity.parentElement.hidden = !quantity;
      if (quantity) {
        for (const name of ['min', 'max', 'step']) {
          const value = quantity.getAttribute(name);
          if (value === null) this.quantity.removeAttribute(name);
          else if (this.quantity.getAttribute(name) !== value) this.quantity.setAttribute(name, value);
        }
        if (document.activeElement !== this.quantity) this.quantity.value = quantity.value;
      }

      const selected = Array.from(this.root.querySelectorAll('variant-selects select option:checked, variant-selects input[type="radio"]:checked'));
      const summary = selected.map(input => input.value).join(' / ');
      if (this.options.textContent !== summary) this.options.textContent = summary;
      this.options.title = summary;
      const sourcePrice = document.getElementById(`price-${this.sectionId}`);
      const onSale = !!sourcePrice?.querySelector('.price--on-sale');
      const currentPrice = sourcePrice?.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      if (currentPrice) this.price.textContent = currentPrice.textContent.trim();
      this.price.parentElement.hidden = !sourcePrice || sourcePrice.classList.contains('hidden');
      const comparePrice = sourcePrice?.querySelector('.price__sale .price-item--regular');
      this.compare.hidden = !onSale || !comparePrice;
      if (comparePrice) this.compare.textContent = comparePrice.textContent.trim();

      const bounds = source.getBoundingClientRect();
      const visible = source.getClientRects().length > 0 && bounds.bottom <= 0;
      this.hidden = !visible;
      this.inert = !visible;
      if (visible) document.body.style.setProperty('--fs-sticky-cart-height', `${this.offsetHeight}px`);

      const error = this.root.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (this.attempted && error?.textContent.trim()) {
        this.attempted = false;
        this.showControls();
      }
    }

    showControls(target) {
      const controls = Array.from(this.root.querySelectorAll('variant-selects select, variant-selects input, [name^="properties["]'));
      const control = target || controls.find(input => input.type !== 'hidden' && input.getClientRects().length > 0) || this.original;
      control?.scrollIntoView({ block: 'center', behavior: 'instant' });
      control?.focus({ preventScroll: true });
      this.schedule();
    }

    add() {
      const source = this.original;
      if (!source || source.disabled || source.getAttribute('aria-disabled') === 'true') return;
      if (this.originalQuantity) {
        if (!this.quantity.reportValidity()) return;
        this.updateQuantity(true);
      }
      const invalid = Array.from(source.form.elements).find(input => input.willValidate && !input.validity.valid);
      if (invalid) {
        this.showControls(invalid);
        invalid.reportValidity();
        return;
      }
      this.attempted = true;
      source.click();
      this.schedule();
    }
  });
}
